import React, { useState } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogContent, DialogTitle, Accordion, AccordionSummary, AccordionDetails, Typography, Alert, List, ListItem, ListItemText } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { makeStyles } from "tss-react/mui";
import DropUpload from "components/Elements/DropUploadBox";
import { BASE_API_PATH, UPLOAD_CSV_NOTE } from "helpers/constants";
import { importSiteAssetRegisters, downloadImportTemplate } from "services/clients/sites/siteAssetRegisters";
import ImportTable from "../components/ImportTable";
import AddDialogStyle from "styles/application/AddDialogStyle";
import { getLocalStorageData } from "helpers/utils";

const AT = AddDialogStyle();

const media = "@media (max-width:414px)";

const defaultData = {
	newRecords: [],
	modifiedRecords: [],
	orphanedRecords: [],
	addedCount: 0,
	modifiedCount: 0,
	orphanedCount: 0,
};

const useStyles = makeStyles()((theme) => ({
	content: {
		display: "flex",
		flexDirection: "column",
		gap: 5,
		marginBottom: 15,
	},
	button: {
		marginTop: 15,
		width: "30%",
		[media]: {
			width: "auto",
		},
	},
	errorMessage: {
		color: "#f44336",
		fontWeight: "normal",
		fontSize: "1.25rem",
		margin: "10px 0",
	},
}));

const ImportDialog = ({
	open,
	closeHandler,
	siteId,
	onImportSuccess,
	getError,
	testIdPrefix,
}) => {
	const { classes } = useStyles();
	const [loading, setLoading] = useState(false);
	const [file, setFile] = useState({});
	const [data, setData] = useState(defaultData);
	const [show, setShow] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [importErrors, setImportErrors] = useState(null);
	const { customCaptions, application } = getLocalStorageData("me");
	const showAssetReferences = application?.showAssetReferences || false;

	const closeOverride = () => {
		setFile({});
		closeHandler();
		setData(defaultData);
		setShow(false);
		setLoading(false);
		setErrorMessage("");
		setImportErrors(null);
	};

	const downloadImportCSVTemplate = async () => {
		try {
			const response = await downloadImportTemplate();
			if (response.status && response.data) {
				const element = document.createElement("a");
				const file = new Blob([response.data], { type: 'text/csv' });
				element.href = URL.createObjectURL(file);
				element.download = "asset-register-import-template.csv";
				document.body.appendChild(element);
				element.click();
				document.body.removeChild(element);
				URL.revokeObjectURL(element.href);
			}
		} catch (err) {
			getError("Failed to download CSV template");
		}
	};

	const importDocument = async (key, shouldImport) => {
		try {
			const response = await importSiteAssetRegisters(siteId, {
				key,
				import: shouldImport,
			});

			if (response?.status) {
				setErrorMessage("");
				setImportErrors(null);
				return response;
			} else {
				const responseData = response?.data;

				if (responseData?.errors && Array.isArray(responseData.errors)) {
					setImportErrors(responseData.errors);
					setErrorMessage("Upload failed due to validation errors");
				} else {
					const errorMsg = response?.data?.detail || response?.data || "Failed to import";
					getError(errorMsg);
					setErrorMessage(typeof errorMsg === 'string' ? errorMsg : "Failed to import");
				}

				return {
					status: false,
					messageType: "Danger",
					data: responseData?.preview || defaultData,
				};
			}
		} catch (err) {
			const responseData = err?.data;

			if (responseData?.errors && Array.isArray(responseData.errors)) {
				setImportErrors(responseData.errors);
				setErrorMessage("Upload failed due to validation errors");
			} else {
				const errorMsg = err?.data?.detail || err?.data || "Failed to import";
				getError(errorMsg);
				setErrorMessage(typeof errorMsg === 'string' ? errorMsg : "Failed to import");
			}
			return {
				status: false,
				messageType: "Danger",
				data: defaultData,
			};
		}
	};

	const onDocumentUpload = async (key, url) => {
		setLoading(true);
		setFile({ key, url });

		try {
			const response = await importDocument(key, false);
			setShow(true);
			setData(response.data || defaultData);
		} catch (err) {
			setErrorMessage("Upload failed");
			setData(defaultData);
		} finally {
			setLoading(false);
		}
	};

	const onImportAsset = async () => {
		setLoading(true);

		try {
			const response = await importDocument(file.key, true);
			if (response.status) {
				await onImportSuccess();
				closeOverride();
			}
		} catch (err) {
			getError("Import failed");
		} finally {
			setLoading(false);
		}
	};

	const {
		newRecords = [],
		modifiedRecords = [],
		orphanedRecords = [],
		addedCount = newRecords.length,
		modifiedCount = modifiedRecords.length,
		orphanedCount = orphanedRecords.length,
	} = data;

	const hasChanges =
		addedCount > 0 ||
		modifiedCount > 0 ||
		orphanedCount > 0;

	return (
		<Dialog
			open={open}
			onClose={closeOverride}
			fullWidth
			maxWidth="md"
			data-testid={testIdPrefix ? `${testIdPrefix}-dialog` : undefined}
		>
			<AT.ActionContainerFileUpload>
				<DialogTitle>
					<AT.HeaderText data-testid={testIdPrefix ? `${testIdPrefix}-title` : undefined}>Import {customCaptions?.assetPlural || "Assets"}</AT.HeaderText>
					<AT.NoteText>{UPLOAD_CSV_NOTE}</AT.NoteText>
				</DialogTitle>
				<AT.NoteButtonContainer>
					<AT.CancelButton data-testid={testIdPrefix ? `${testIdPrefix}-cancel` : undefined} variant="contained" onClick={closeOverride}>
						Cancel
					</AT.CancelButton>
				</AT.NoteButtonContainer>
			</AT.ActionContainerFileUpload>

			<DialogContent>
				<div className={classes.content}>
					{importErrors && (
						<Alert severity="error" style={{ marginBottom: "16px" }}>
							<Typography variant="h6" gutterBottom>
								Import {customCaptions?.assetPlural || "Assets"} Failed
							</Typography>
							<Typography variant="body2" gutterBottom>
								The following validation errors were found:
							</Typography>
							<List dense>
								{importErrors.map((error, index) => (
									<ListItem key={index} style={{ paddingLeft: 0, paddingTop: 0, paddingBottom: 0 }}>
										<ListItemText
											primary={error}
											primaryTypographyProps={{ variant: "body2" }}
										/>
									</ListItem>
								))}
							</List>
						</Alert>
					)}

					{!show && (
						<DropUpload
							testId={testIdPrefix ? `${testIdPrefix}-dropzone` : undefined}
							filesUploading={loading}
							setFilesUploading={setLoading}
							isImageUploaded={false}
							uploadReturn={onDocumentUpload}
							apiPath={`${BASE_API_PATH}SiteAssetRegisters/${siteId}/uploadList`}
						/>
					)}

				{show && hasChanges && (
					<Alert data-testid={testIdPrefix ? `${testIdPrefix}-summary` : undefined} severity="info" style={{ marginTop: "16px" }}>
						<Typography variant="subtitle2">
							{[
								addedCount > 0 && `${addedCount} new`,
								modifiedCount > 0 && `${modifiedCount} modified`,
								orphanedCount > 0 && `${orphanedCount} orphaned`,
							].filter(Boolean).join(", ")} {customCaptions?.assetPlural?.toLowerCase() || "assets"}
						</Typography>
					</Alert>
				)}

				{show && (addedCount > 0 || modifiedCount > 0) && (
					<Accordion data-testid={testIdPrefix ? `${testIdPrefix}-preview` : undefined} defaultExpanded={!importErrors} style={{ marginTop: "16px" }}>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}>
							<Typography variant="subtitle1">
								Import Preview - {addedCount + modifiedCount} items will be imported
							</Typography>
						</AccordionSummary>
						<AccordionDetails style={{ padding: "8px 16px" }}>
							<ImportTable
								title={`New ${customCaptions?.assetPlural || "Assets"} (${addedCount})${addedCount > newRecords.length ? ` — showing first ${newRecords.length}` : ""}`}
								data={newRecords}
								customCaptions={customCaptions}
								showAssetReferences={showAssetReferences}
								testIdPrefix={testIdPrefix ? `${testIdPrefix}-new-records` : undefined}
							/>
							<ImportTable
								title={`Modified ${customCaptions?.assetPlural || "Assets"} (${modifiedCount})${modifiedCount > modifiedRecords.length ? ` — showing first ${modifiedRecords.length}` : ""}`}
								data={modifiedRecords}
								customCaptions={customCaptions}
								showAssetReferences={showAssetReferences}
								testIdPrefix={testIdPrefix ? `${testIdPrefix}-modified-records` : undefined}
							/>
						</AccordionDetails>
					</Accordion>
				)}

				{show && orphanedCount > 0 && (
					<Accordion data-testid={testIdPrefix ? `${testIdPrefix}-orphans` : undefined} style={{ marginTop: "16px" }}>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}>
							<Typography variant="subtitle1" style={{ color: "#ed6c02" }}>
								Orphaned {customCaptions?.assetPlural || "Assets"} ({orphanedCount})
							</Typography>
						</AccordionSummary>
						<AccordionDetails style={{ padding: "8px 16px" }}>
							<Alert severity="warning" style={{ marginBottom: "8px" }}>
								<Typography variant="body2">
									These records have no parent in the register and will appear as standalone assets.
								</Typography>
							</Alert>
							<ImportTable
								title={orphanedCount > orphanedRecords.length ? `Showing first ${orphanedRecords.length}` : ""}
								data={orphanedRecords}
								customCaptions={customCaptions}
								showAssetReferences={showAssetReferences}
								testIdPrefix={testIdPrefix ? `${testIdPrefix}-orphan-records` : undefined}
							/>
						</AccordionDetails>
					</Accordion>
				)}

					{show && !hasChanges && !importErrors && (
						<Typography variant="h6" style={{ marginTop: "16px" }}>No changes will be made</Typography>
					)}
				</div>

				<div style={{ height: "20px" }} />

				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<span
						data-testid={testIdPrefix ? `${testIdPrefix}-download-template` : undefined}
						style={{ cursor: "pointer", fontSize: "16px", color: "#307AD6", textDecoration: "underline" }}
						onClick={downloadImportCSVTemplate}
					>
						Download Import CSV Template
					</span>

					{show && hasChanges && (
						<AT.ConfirmButton
							data-testid={testIdPrefix ? `${testIdPrefix}-confirm` : undefined}
							variant="contained"
							onClick={onImportAsset}
							disabled={loading}
						>
							{loading ? "Importing..." : "Import"}
						</AT.ConfirmButton>
					)}
				</div>

				<div style={{ height: "10px" }} />
			</DialogContent>
		</Dialog>
	);
};

ImportDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	closeHandler: PropTypes.func.isRequired,
	siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	onImportSuccess: PropTypes.func.isRequired,
	getError: PropTypes.func.isRequired,
	testIdPrefix: PropTypes.string,
};

export default ImportDialog;
